import React, { FC } from 'react';
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import TableTab from '../../../components/tabs/table-tab';
import allReportStatus from '../../../constants/all-report-status';
import { ReportCategories, ReportTypes } from '../../../constants/report-types';
import { ReportCategoryName } from '../../../constants/system-constants';
import { useLocale } from '../../../contexts/localeContext';
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import CustomSettingIcon from '../../../images/Custom Setting.svg';
import InheritPresetIcon from '../../../images/Inherited from Preset.svg';
import { Category } from '../../../models/Category';
import columns from '../../../models/ColumnDefinitions/ReportColumn';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { Report } from '../../../models/Report';
import searchColumns from '../../../models/search/ReportSearch';
import ReportTypeSearch from '../../../models/search/ReportTypeSearch';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { TableLegendItem } from '../../../models/table/TableLegendItem';
import { deleteReport, getAllReport, getAllReportTypeNamesWithPagination, getReportById } from '../../../services/reportService';
import { showError } from '../../../services/utilities/ServiceError';
import { appendFilter } from '../../../utilities/filters';
import { getItemPath } from '../../../utilities/reportUtil';
import { IAppState } from '@/store/types';

interface Props {
    isModalOpen: boolean;
    openModal: (isOpen: boolean) => void;
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setCurrentItem: (item: Report) => void;
    reportCategoryName?: ReportCategoryName.TRANSACTIONAL | ReportCategoryName.TIME_AND_ATTENDANCE | ReportCategoryName.ACCESS_CONTROL | ReportCategoryName.SYSTEM_SETUP | ReportCategoryName.EMERGENCY_AND_SAFETY | ReportCategoryName.COMPLIANCE | ReportCategoryName.MY_REPORTS;
    userProfileId?: number;
}

const AllReport: FC<Props> = ({ setCurrentItem, setBusy, categories, setCategories, openModal, reportCategoryName, userProfileId }) => {
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));

    const lookup: Lookup[] = [
        {
            name: LookupTypes.reportType,
            values: [],
            dataMethod: getAllReportTypeNamesWithPagination,
            searchAccessor: ReportTypeSearch.find(item => item.accessor === "type")?.searchOnName
        }
    ];

    const tableLegendItems: TableLegendItem[] = [
        {
            value: allReportStatus.Custom.id,
            imageIcon: CustomSettingIcon,
            label: translate.byKey(allReportStatus.Custom.translationKey) ?? allReportStatus.Custom.label,
            tooltip: translate.byKey(allReportStatus.Custom.translationKey) ?? allReportStatus.Custom.label
        },
        {
            value: allReportStatus.Template.id,
            imageIcon: InheritPresetIcon,
            label: translate.byKey(allReportStatus.Template.translationKey) ?? allReportStatus.Template.label,
            tooltip: translate.byKey(allReportStatus.Template.translationKey) ?? allReportStatus.Template.label
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteReport(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const editReport = async (id: string) => {
        try {
            const timeTriggeredAction = await getReportById(id);
            setCurrentItem(timeTriggeredAction)
            openModal(true);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const newReportSearchColumns = searchColumns.filter(column => {
        return column.accessor !== 'type'
    });

    const newReportColumns = columns.map(column => {
        return { ...column, active: column.accessor === 'type' ? false : column.active }
    });

    const getTitle = () => {
        switch (reportCategoryName) {
            case ReportCategoryName.TRANSACTIONAL:
                return translate.byKey(TranslationKeys.transactions);
            case ReportCategoryName.TIME_AND_ATTENDANCE:
                return translate.byKey(TranslationKeys.timeAndAttendance);
            case ReportCategoryName.ACCESS_CONTROL:
                return translate.byKey(TranslationKeys.accessControl);
            case ReportCategoryName.SYSTEM_SETUP:
                return translate.byKey(TranslationKeys.systemSetup);
            case ReportCategoryName.EMERGENCY_AND_SAFETY:
                return translate.byKey(TranslationKeys.emergencyAndSafety);
            case ReportCategoryName.COMPLIANCE:
                return translate.byKey(TranslationKeys.compliance);
            case ReportCategoryName.MY_REPORTS:
                return translate.byKey(TranslationKeys.myReports);
            default:
                return translate.byKey(TranslationKeys.allReports);
        }
    }

    const getDataMethod = (listParameters) => {
        switch (reportCategoryName) {
            case ReportCategoryName.TRANSACTIONAL:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportCategories.TRANSACTION], 'Report category', 'category', listParameters?.filters ?? []) }, user.profileId);
            case ReportCategoryName.TIME_AND_ATTENDANCE:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportCategories.TIME_AND_ATTENDANCE], 'Report category', 'category', listParameters?.filters ?? []) }, user.profileId);
            case ReportCategoryName.ACCESS_CONTROL:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportCategories.ACCESS_CONTROL], 'Report category', 'category', listParameters?.filters ?? []) }, user.profileId);
            case ReportCategoryName.SYSTEM_SETUP:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportCategories.SYSTEM_SETUP], 'Report category', 'category', listParameters?.filters ?? []) }, user.profileId);
            case ReportCategoryName.EMERGENCY_AND_SAFETY:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportCategories.EMERGENCY_AND_SAFETY], 'Report category', 'category', listParameters?.filters ?? []) }, userProfileId);
            case ReportCategoryName.COMPLIANCE:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportTypes.SEQUENTIAL_EVENT, ReportTypes.OPERATIONAL_COMPLIANCE], 'Report type', 'reportType.id', listParameters?.filters ?? []) }, user.profileId);
            case ReportCategoryName.MY_REPORTS:
                return getAllReport({...listParameters, filters: appendFilter(user.id, "Created By Current User","userLogin.id",[],"=in=")} , userProfileId);
            default:
                return getAllReport(listParameters, user.profileId);
        }
    }

    const getTableDescriptor = () => {
        switch (reportCategoryName) {
            case ReportCategoryName.TRANSACTIONAL:
            case ReportCategoryName.TIME_AND_ATTENDANCE:
            case ReportCategoryName.ACCESS_CONTROL:
            case ReportCategoryName.SYSTEM_SETUP:
            case ReportCategoryName.EMERGENCY_AND_SAFETY:
            case ReportCategoryName.COMPLIANCE:
                return newReportColumns;
            default:
                return columns;
        }
    }

    const getSearchDescriptor = () => {
        switch (reportCategoryName) {
            case ReportCategoryName.TRANSACTIONAL:
            case ReportCategoryName.TIME_AND_ATTENDANCE:
            case ReportCategoryName.ACCESS_CONTROL:
            case ReportCategoryName.SYSTEM_SETUP:
            case ReportCategoryName.EMERGENCY_AND_SAFETY:
            case ReportCategoryName.COMPLIANCE:
                return newReportSearchColumns;
            default:
                return searchColumns;
        }
    }

    return <TableContextProvider
        dataMethod={(listParameters) => getDataMethod(listParameters)}
        tableDescriptor={getTableDescriptor()}
        searchDescriptor={getSearchDescriptor()}
        legendItems={tableLegendItems}
        useLegend
        useTable
        usePagination
        useSort
        useSearch
        deleteMethod={onDelete}
        editMethod={editReport}
        useFilter
        lookup={lookup}
        customDelete={{ fieldName: "status", comparisonValue: allReportStatus.Custom.id }}
        customEdit={{ fieldName: "status", comparisonValue: allReportStatus.Custom.id }} 
        onAddNew={() => openModal(true)}>
        <TableTab
            title={getTitle()}
            selectRows={false}
            rowPath={getItemPath(reportCategoryName ?? '', true)} 
            cyName="allReport"/>
    </TableContextProvider>;
}

export default AllReport;
